import { useCallback, useEffect, useRef } from "react";
import styles from "./fwRichTextarea.module.scss";

const FW_CLASS = styles.fw as string;

/**
 * Textarea replacement that renders text inside `<fw>…</fw>` blocks in a
 * monospace font (with subtle background) while keeping the rest of the
 * text in the regular sans-serif. Useful for product descriptions that mix
 * flowing copy with column-aligned tables (nutrition facts, specs, etc.).
 *
 * The value prop is plain text including the literal `<fw>` / `</fw>`
 * markers — the device decides how to render them at display time.
 * Implementation uses contenteditable; on each input we rebuild inline
 * spans and restore the caret at the same plain-text offset.
 */
interface Props {
  /** Plain text incl. literal `<fw>` markers. Null/undefined is treated as empty. */
  value: string | null | undefined;
  onChange: (value: string) => void;
  rows?: number;
  className?: string;
  "aria-label"?: string;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function renderHtml(text: string, fwClass: string): string {
  if (!text) return "<br>";
  let out = "";
  let cursor = 0;
  const lower = text.toLowerCase();
  while (cursor < text.length) {
    const open = lower.indexOf("<fw>", cursor);
    if (open < 0) {
      out += escapeHtml(text.slice(cursor));
      break;
    }
    if (open > cursor) out += escapeHtml(text.slice(cursor, open));
    const close = lower.indexOf("</fw>", open + 4);
    if (close < 0) {
      out += `<span class="${fwClass}">${escapeHtml(text.slice(open))}</span>`;
      break;
    }
    const end = close + 5;
    out += `<span class="${fwClass}">${escapeHtml(text.slice(open, end))}</span>`;
    cursor = end;
  }
  // A value ending in a newline renders NO final line under white-space:
  // pre-wrap, so pressing Enter at the end looked like it did nothing: the
  // caret had nowhere visible to go, and the next readPlainText() stripped the
  // newline straight back out of the value. A filler <br> makes that last line
  // real. Measured: without it the editor stays 20px tall and "line1\n" round
  // trips to "line1"; with it, 40px and "line1\n" survives.
  if (text.endsWith("\n")) out += "<br>";
  return out;
}

function getCaretOffset(root: HTMLElement): number | null {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return null;
  const range = sel.getRangeAt(0);
  if (!root.contains(range.endContainer)) return null;
  const pre = range.cloneRange();
  pre.selectNodeContents(root);
  pre.setEnd(range.endContainer, range.endOffset);
  return pre.toString().length;
}

function setCaretOffset(root: HTMLElement, offset: number) {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let node: Node | null = walker.nextNode();
  let remaining = offset;
  while (node) {
    const len = node.textContent?.length ?? 0;
    if (remaining <= len) {
      const range = document.createRange();
      range.setStart(node, remaining);
      range.collapse(true);
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(range);
      return;
    }
    remaining -= len;
    node = walker.nextNode();
  }
  const range = document.createRange();
  range.selectNodeContents(root);
  range.collapse(false);
  const sel = window.getSelection();
  sel?.removeAllRanges();
  sel?.addRange(range);
}

function readPlainText(el: HTMLElement): string {
  // innerText preserves layout-induced newlines (div/br inserted by
  // contenteditable on Enter). Strip a single trailing newline that
  // Chrome appends when the last child is a <br>.
  const raw = el.innerText ?? el.textContent ?? "";
  return raw.replace(/\n$/, "");
}

export function FwRichTextarea({ value, onChange, rows = 4, className, "aria-label": ariaLabel }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const lastValue = useRef<string>("");
  const text = value ?? "";

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (text === lastValue.current) return;
    lastValue.current = text;
    el.innerHTML = renderHtml(text, FW_CLASS);
  }, [text]);

  const handleInput = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const offset = getCaretOffset(el);
    const text = readPlainText(el);
    const html = renderHtml(text, FW_CLASS);
    lastValue.current = text;
    if (el.innerHTML !== html) {
      el.innerHTML = html;
      if (offset != null) setCaretOffset(el, offset);
    }
    onChange(text);
  }, [onChange]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "Enter") return;
    // Chrome's default Enter runs insertParagraph, which wraps the rest of the
    // value in a <div>. Measured: "aaa|\nbbb" + Enter becomes
    // `aaa<div>\nbbb</div>` — a block boundary that innerText counts as a
    // newline but Range.toString() does not. So one Enter added TWO newlines to
    // the value, while getCaretOffset came out one short and put the caret
    // before the break instead of after it. insertLineBreak keeps everything in
    // text nodes as a literal "\n", which both readers agree on.
    e.preventDefault();
    if (document.execCommand("insertLineBreak")) return;

    // Fallback for engines without insertLineBreak. Manual DOM edits fire no
    // input event, so the change has to be pushed through by hand.
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    range.deleteContents();
    const node = document.createTextNode("\n");
    range.insertNode(node);
    range.setStartAfter(node);
    range.collapse(true);
    sel.removeAllRanges();
    sel.addRange(range);
    handleInput();
  }, [handleInput]);

  const handlePaste = useCallback((e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    const text = e.clipboardData.getData("text/plain");
    // Fall back to insertText — execCommand is deprecated but still the only
    // reliable way to insert plain text at caret inside a contenteditable.
    document.execCommand("insertText", false, text);
  }, []);

  return (
    <div
      ref={ref}
      contentEditable
      suppressContentEditableWarning
      spellCheck
      role="textbox"
      aria-multiline="true"
      aria-label={ariaLabel}
      className={`${styles.editor}${className ? " " + className : ""}`}
      style={{ minHeight: `${Math.max(rows, 2) * 1.45}em` }}
      onInput={handleInput}
      onKeyDown={handleKeyDown}
      onPaste={handlePaste}
    />
  );
}
